import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Hash, Phone, Mail } from 'lucide-react';
import toast from 'react-hot-toast';

const MOCK_DATA = [
  { id: 1, name: 'Mohit Saini', email: 'mohit@example.com', phone: '+91 99999 88888', role: 'Uttar Pradesh Representative' },
  { id: 2, name: 'Vijay Chawla', email: 'vijay@example.com', phone: '+91 88888 77777', role: 'Gujarat Representative' }
];

const COLUMNS = [
  { header: 'Full Name', accessor: 'name' },
  { header: 'Email Address', accessor: 'email' },
  { header: 'Phone Number', accessor: 'phone' },
  { header: 'Designation', accessor: 'role' }
];

const FORM_FIELDS = [
  { label: 'Full Name', accessor: 'name', placeholder: 'Enter official name' },
  { label: 'Email Address', accessor: 'email', type: 'email', placeholder: 'Enter official email' },
  { label: 'Phone Number', accessor: 'phone', type: 'tel', placeholder: 'Enter official contact number' },
  { label: 'Designation', accessor: 'role', placeholder: 'e.g. Maharashtra State President' }
];

export default function Contacts() {
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Search & Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 5;

  const fetchData = async () => {
    setIsLoading(true);
    setTimeout(() => {
      setDataList(MOCK_DATA);
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchData();
  }, []);



  const filteredData = useMemo(() => {
    let result = dataList;
    if (debouncedQuery) {
      result = result.filter(item => 
        Object.values(item).some(val => 
          String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
        )
      );
    }
    return result;
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    return [
      { label: "Official Contacts", value: total, icon: <Phone size={20} />, color: 'blue' },
      { label: "Valid Emails", value: dataList.filter(d => d.email).length, icon: <Mail size={20} />, color: 'orange' },
      { label: "Unique Designations", value: new Set(dataList.map(d => d.role).filter(Boolean)).size, icon: <Hash size={20} />, color: 'green' }
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFormSubmit = async (formData) => {
    if (editingData) {
      setDataList(prev => prev.map(item => item.id === editingData.id ? { ...item, ...formData } : item));
      toast.success('Official contact updated successfully!');
    } else {
      const newItem = { ...formData, id: Date.now() };
      setDataList(prev => [newItem, ...prev]);
      toast.success('Official contact added successfully!');
    }
    setIsFormOpen(false);
    setEditingData(null);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    setIsDeleteModalOpen(false);
    setDataList(prev => prev.filter(item => item.id !== deleteTarget.id));
    toast.success('Contact deleted successfully!');
    setDeleteTarget(null);
  };

  return (
    <div className="page-content">
      <PageHeader 
        title="Official State Contacts"
        subtitle="Manage designated contact details of state presidents and representatives"
        onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
        addLabel="Add Contact"
        kpis={kpis}
      />
      
      {isFormOpen && (
        <DataForm 
          title={editingData ? `Edit Official Contact` : `Add New Contact`} 
          fields={FORM_FIELDS} 
          initialData={editingData}
          onSubmit={handleFormSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      )}
      
      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar 
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable 
            columns={COLUMNS} 
            data={paginatedData} 
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Official Contact"
        message="Are you sure you want to delete this official representative contact?"
        itemName={deleteTarget ? `${deleteTarget.name} (${deleteTarget.role})` : ''}
      />
    </div>
  );
}
